import {useCallback,useEffect,useRef,useState} from 'react';
import type {VisualizeEntitlement} from '@reados/contracts';
import {api,errorMessage} from '../api';
import {BALANCE_UPDATED_EVENT} from '../top-up';
import {DESKTOP_ENTITLEMENTS_UPDATED} from '../desktop-commerce-api';

/** One account-owned controller shared by the library and composer panels. */
export function useVisualizeSettings(userId:string|undefined){
  const [value,setValue]=useState<VisualizeEntitlement>();
  const [error,setError]=useState(''),[busy,setBusy]=useState(false);
  const epoch=useRef(0),read=useRef<AbortController|null>(null),write=useRef(false);
  const owner=useRef(userId);owner.current=userId;
  const refresh=useCallback(async()=>{
    if(!userId||write.current)return;
    const ticket=++epoch.current,controller=new AbortController();read.current?.abort();read.current=controller;
    try{
      const next=await api.visualize(controller.signal);
      if(owner.current===userId&&ticket===epoch.current){setValue(next);setError('');}
    }catch(reason){if(owner.current===userId&&ticket===epoch.current&&!controller.signal.aborted)setError(errorMessage(reason));}
    finally{if(read.current===controller)read.current=null;}
  },[userId]);
  const toggle=useCallback(async(enabled:boolean)=>{
    if(!userId||owner.current!==userId)throw new Error('登录状态已变化，请重新操作。');
    if(write.current)throw new Error('插件设置正在更新，请稍候。');
    write.current=true;const ticket=++epoch.current;read.current?.abort();setBusy(true);setError('');
    try{
      const next=await api.setVisualize(enabled);
      if(owner.current!==userId||ticket!==epoch.current)throw new Error('登录状态已变化，请重新操作。');
      setValue(next);return next;
    }catch(reason){if(owner.current===userId&&ticket===epoch.current)setError(errorMessage(reason));throw reason;}
    finally{if(owner.current===userId&&ticket===epoch.current){write.current=false;setBusy(false);void refresh();}}
  },[userId,refresh]);
  useEffect(()=>{
    epoch.current++;read.current?.abort();write.current=false;setValue(undefined);setError('');setBusy(false);
    if(!userId)return;
    void refresh();
    const visible=()=>{if(document.visibilityState==='visible')void refresh();};
    const redeemed=(event:Event)=>{if((event as CustomEvent).detail?.userId===userId)void refresh();};
    window.addEventListener('focus',visible);window.addEventListener('online',visible);document.addEventListener('visibilitychange',visible);
    window.addEventListener(BALANCE_UPDATED_EVENT,redeemed);window.addEventListener(DESKTOP_ENTITLEMENTS_UPDATED,redeemed);
    const timer=window.setInterval(visible,30_000);
    return()=>{epoch.current++;read.current?.abort();write.current=false;window.clearInterval(timer);window.removeEventListener('focus',visible);window.removeEventListener('online',visible);document.removeEventListener('visibilitychange',visible);window.removeEventListener(BALANCE_UPDATED_EVENT,redeemed);window.removeEventListener(DESKTOP_ENTITLEMENTS_UPDATED,redeemed);};
  },[userId,refresh]);
  return {value,error,busy,refresh,toggle,supported:true};
}
